import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import RecentOrderCard from './RecentOrderCard';
import EmptyState from './EmptyState';
import { Search } from 'lucide-react';
import { apiFetch } from '../../utils/api';

const OrdersTab = ({ onViewOrderDetails }) => {
  const { user } = useContext(AuthContext);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeStatusFilter, setActiveStatusFilter] = useState('All');

  const statusFilters = ['All', 'Pending', 'Confirmed', 'Packed', 'Shipped', 'Delivered', 'Cancelled'];

  useEffect(() => {
    if (!user) return;
    const fetchOrders = async () => {
      try {
        setLoading(true);
        const res = await apiFetch('/api/orders/myorders');
        const data = await res.json();
        if (res.ok) {
          setOrders(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error('Failed to fetch orders:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [user]);

  // Filter logic
  const filteredOrders = orders.filter(order => {
    // 1. Search Query filter (matches orderNumber or _id)
    const matchesSearch = 
      (order.orderNumber && order.orderNumber.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (order._id && order._id.toLowerCase().includes(searchQuery.toLowerCase()));

    // 2. Status Badge filter
    const matchesStatus = 
      activeStatusFilter === 'All' || 
      order.status === activeStatusFilter || 
      (activeStatusFilter === 'Pending' && order.status === 'Pending');

    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <h2 style={{ color: 'var(--color-text-primary)', fontSize: '1.8rem', margin: 0 }}>My Orders</h2>
        <div className="orders-filters-row">
          <div className="skeleton" style={{ height: '40px', width: '320px' }} />
          <div className="skeleton" style={{ height: '40px', width: '450px' }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div className="skeleton skeleton-row" />
          <div className="skeleton skeleton-row" />
          <div className="skeleton skeleton-row" />
          <div className="skeleton skeleton-row" />
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      
      {/* Tab Title */}
      <h2 style={{ color: 'var(--color-text-primary)', fontSize: '1.8rem', margin: 0 }}>My Orders</h2>

      {/* Filters & Search Row */}
      <div className="orders-filters-row">
        <div className="orders-search-wrapper">
          <Search size={18} className="orders-search-icon" />
          <input
            type="text"
            className="orders-search-input"
            placeholder="Search by order number..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="orders-status-badges-group">
          {statusFilters.map(status => (
            <button
              key={status}
              onClick={() => setActiveStatusFilter(status)}
              className={`orders-status-tab ${activeStatusFilter === status ? 'active' : ''}`}
            >
              {status}
            </button>
          ))}
        </div>
      </div>

      {/* Orders List / Empty State */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '10px' }}>
        {filteredOrders.length === 0 ? (
          <EmptyState
            icon="📦"
            title={orders.length === 0 ? "No Orders Yet" : "No Matching Orders Found"}
            description={
              orders.length === 0 
                ? "Your orders will appear here once you place them." 
                : "Try adjusting your search query or status filter parameters to find what you are looking for."
            }
            actionText={orders.length === 0 ? "Go to Storefront" : "Reset Filters"}
            onActionClick={
              orders.length === 0 
                ? () => window.location.href = '/shop' 
                : () => { setSearchQuery(''); setActiveStatusFilter('All'); }
            }
          />
        ) : (
          filteredOrders.map(order => (
            <RecentOrderCard
              key={order._id}
              order={order}
              onViewDetails={onViewOrderDetails}
            />
          ))
        )}
      </div>

    </div>
  );
};

export default OrdersTab;
